import * as React from 'react';
import { useEffect, useState } from 'react'

import CreateAClassInlineForm from './create_a_class_inline_form'

import { canvasProvider, cleverProvider, classlinkProvider, googleProvider, providerConfigLookup } from '../../../classrooms/providerHelpers'
import { requestGet } from '../../../../../../modules/request/index'
import { DataTable, Tooltip, Snackbar, Checkbox, INDETERMINATE, defaultSnackbarTimeout, lockedIcon, ButtonSpinner, DropdownInput, EXTRA_SMALL_ICON_BASE_SRC } from '../../../../../Shared/index'
import useSnackbarMonitor from '../../../../../Shared/hooks/useSnackbarMonitor'
import pusherInitializer from '../../../../../../modules/pusherInitializer'
import ImportProviderClassroomsModal from '../../../classrooms/import_provider_classrooms_modal'
import LinkProviderAccountModal from '../../../classrooms/link_provider_account_modal'
import NoClassroomsToImportModal from '../../../classrooms/no_classrooms_to_import_modal'
import ReauthorizeProviderModal from '../../../classrooms/reauthorize_provider_modal'
import CanvasModal from '../../../classrooms/canvas_modal'
import ClasslinkModal from '../../../classrooms/classlink_modal';
import { hasSchoolOrDistrictPremium } from '../../../classrooms/userSubscription';

const emptyClassSrc = `${window.postbuildEnv.CDN_URL}/images/illustrations/empty_class.svg`

export const createAClassForm = 'createAClassForm'

export const importProviderClassroomsModal = 'importProviderClassroomsModal'
export const linkCanvasAccountModal = 'linkCanvasAccountModal'
export const linkCleverAccountModal = 'linkCleverAccountModal'
export const linkGoogleAccountModal = 'linkGoogleAccountModal'
export const linkClasslinkAccountModal = 'linkClasslinkAccountModal'
export const reauthorizeProviderModal = 'reauthorizeProviderModal'
export const noClassroomsToImportModal = 'noClassroomsToImportModal'

const headers = [
  {
    name: '',
    attribute: 'checkbox',
    width: '50px',
    headerClassName: 'classroom-checkbox-cell',
    rowSectionClassName: 'classroom-checkbox-cell',
    noTooltip: true
  },
  {
    name: 'Class',
    attribute: 'name',
    width: '706px'
  },
  {
    name: 'Students',
    attribute: 'students',
    width: '360px'
  }
]

function renderClassroomCheckbox(classroom, students, toggleClassroomSelection, lockedClassroomIds, lockedMessage) {
  const { emptyClassroomSelected, } = classroom

  if (lockedClassroomIds.includes(classroom.id)) {
    return <Tooltip tooltipText={lockedMessage} tooltipTriggerText={<img alt={lockedIcon.alt} src={lockedIcon.src} />} />
  }

  const selectedStudents = students && students.length ? students.filter(s => s.isSelected) : []

  return (
    <Checkbox
      ariaLabel={classroom.name}
      onClick={() => toggleClassroomSelection(classroom)}
      selected={emptyClassroomSelected || !!selectedStudents.length}
    />
  )
}

function renderStudentSection(classroom, students, lockedClassroomIds, toggleStudentSelection) {
  function selectStudents(studentOptions, classroomId) {
    const studentIds = studentOptions.map(s => s.value)
    toggleStudentSelection(studentIds, classroomId)
  }

  const { id, emptyClassroomSelected, } = classroom

  if (lockedClassroomIds.includes(id)) { return null }

  const options = students ? students.map((s) => {
    return { value: s.id, label: s.name, isSelected: s.isSelected, }
  }) : []

  const selectedStudents = options.filter(s => s.isSelected)

  if (options.length) {
    return (
      <DropdownInput
        ariaLabel="Students"
        className="borderless extra-small focus-on-light"
        handleChange={(e) => { selectStudents(e, id) }}
        isMulti={true}
        isSearchable={false}
        options={options}
        optionType="student"
        value={selectedStudents}
      />
    )
  } else if (emptyClassroomSelected) {
    return <span className="empty-class-students">And all students who join in the future</span>
  }
}


const AssignStudents = ({
  classrooms,
  canvasLink,
  cleverLink,
  fetchClassrooms,
  googleLink,
  lockedClassroomIds,
  lockedMessage,
  toggleClassroomSelection,
  toggleStudentSelection,
  user
}) => {
  const { provider } = user
  const providerConfig = providerConfigLookup[provider]

  const reauthorizeLink = {
    [canvasProvider]: canvasLink,
    [cleverProvider]: cleverLink,
    [googleProvider]: googleLink,
  }[provider]

  const [providerClassrooms, setProviderClassrooms] = useState([])
  const [providerClassroomsLoading, setProviderClassroomsLoading] = useState(false)
  const [pendingImportFromProviderRequest, setPendingImportFromProviderRequest] = useState(false)

  const [isSnackbarVisible, setIsSnackbarVisible] = useState(false)
  const [snackbarCopy, setSnackbarCopy] = useState('')
  const [visibleModal, setVisibleModal] = useState(null)

  useSnackbarMonitor(isSnackbarVisible, setIsSnackbarVisible, defaultSnackbarTimeout)

  useEffect(() => {
    if (!provider) { return }

    retrieveProviderClassrooms()
  }, [])

  useEffect(() => {
    if (!pendingImportFromProviderRequest) { return }
    if (providerClassrooms === null) { return }

    setPendingImportFromProviderRequest(false)
    openModal(providerClassrooms.length ? importProviderClassroomsModal : noClassroomsToImportModal)
  }, [providerClassrooms])

  function handleImportClassesClick(option) {
    return importFromProvider[option.value]()
  }

  const closeModal = (callback = null) => {
    setVisibleModal(null)

    if (callback && typeof (callback) === 'function') { callback() }
  }

  const importFromCanvas = () => {
    if (providerConfig?.isCanvas) {
      setPendingImportFromProviderRequest(true)
      retrieveProviderClassrooms()
    } else {
      openModal(linkCanvasAccountModal)
    }
  }

  const importFromClever = () => {
    if (providerConfig?.isClever) {
      setPendingImportFromProviderRequest(true)
      retrieveProviderClassrooms()
    } else {
      openModal(linkCleverAccountModal)
    }
  }

  const importFromGoogle = () => {
    if (providerConfig?.isGoogle) {
      setPendingImportFromProviderRequest(true)
      retrieveProviderClassrooms()
    } else {
      openModal(linkGoogleAccountModal)
    }
  }

  const importFromClasslink = () => {
    openModal(linkClasslinkAccountModal)
  }

  const importFromProvider = {
    [canvasProvider]: importFromCanvas,
    [cleverProvider]: importFromClever,
    [googleProvider]: importFromGoogle,
    [classlinkProvider]: importFromClasslink
  }

  const onSuccess = (snackbarCopy: string) => {
    fetchClassrooms()
    retrieveProviderClassrooms()
    showSnackbar(snackbarCopy)
    closeModal()
  }

  const openModal = (modalName: string) => {
    setVisibleModal(modalName)
  }

  const retrieveProviderClassrooms = () => {
    if (providerConfig?.isClasslink) { return } // Classlink assets are imported through a different flow

    setProviderClassroomsLoading(true)
    pusherInitializer(user.id, providerConfig.retrieveClassroomsEventName, retrieveProviderClassrooms)

    requestGet(providerConfig.retrieveClassroomsPath, (body) => {
      if (body.reauthorization_required) {
        openModal(reauthorizeProviderModal)
        return
      }

      if (body.quill_retrieval_processing) { return }

      if (providerConfig?.isGoogle) {
        setProviderClassrooms(body.classrooms.filter(classroom => !classroom.alreadyImported && classroom.is_owner))
      } else {
        setProviderClassrooms(body.classrooms.filter(classroom => !classroom.alreadyImported))
      }

      setProviderClassroomsLoading(false)
    })
  }

  const showSnackbar = (snackbarCopy: string) => {
    setSnackbarCopy(snackbarCopy)
    setIsSnackbarVisible(true)
  }

  const renderAllClassroomsCheckbox = () => {
    if (classrooms.length <= 1) { return null }

    const selectedClassrooms = classrooms.filter((c) => {
      const { students, classroom, } = c
      const selectedStudents = students && students.length ? students.filter(s => s.isSelected) : []
      return !!(classroom.emptyClassroomSelected || selectedStudents.length)
    })

    const label = 'All classes and students'

    let checkbox = <Checkbox label={label} onClick={() => toggleClassroomSelection(null, true)} selected={false} />

    if (selectedClassrooms.length === classrooms.length) {
      checkbox = (<Checkbox label={label} onClick={() => toggleClassroomSelection(null, false)} selected={true} />)
    } else if (selectedClassrooms.length) {
      checkbox = (<Checkbox label={label} onClick={() => toggleClassroomSelection(null, false)} selected={true} state={INDETERMINATE} />)
    }

    return (
      <div className="all-classes-checkbox">
        {checkbox}
      </div>
    )
  }

  const renderClassroomList = () => {
    if (classrooms && classrooms.length) {
      const rows = classrooms.map(c => {
        const { classroom, students} = c

        return {
          id: classroom.id,
          checkbox: renderClassroomCheckbox(classroom, students, toggleClassroomSelection, lockedClassroomIds, lockedMessage),
          name: classroom.name,
          students: renderStudentSection(classroom, students, lockedClassroomIds, toggleStudentSelection)
        }
      })
      return (
        <DataTable
          headers={headers}
          reportingFormat={true}
          rows={rows}
        />
      )
    } else if (visibleModal !== createAClassForm) {
      return (
        <div className="no-active-classes">
          <img alt="empty class" src={emptyClassSrc} />
          <p>Your classrooms will appear here. Add a class to get started.</p>
        </div>
      )
    }
  }

  const renderClassroomsSection = () => {
    return (
      <div className="assignment-section">
        <div className="assignment-section-header assign-students">
          <div className="number-and-name">
            <span className="assignment-section-number">3</span>
            <span className="assignment-section-name" id="assignStudentsHeader">Choose classes or students</span>
          </div>
          <div className="import-or-create-classroom-buttons">
            {provider ? renderImportFromProviderButton(provider) : renderImportClassesDropdown()}
            <button
              className="quill-button contained small green focus-on-light create-a-class-button"
              onClick={() => openModal(createAClassForm)}
              type="button"
            >
              Create a class
            </button>
          </div>
        </div>
        <div className="assignment-section-body">
          {renderCreateAClassInlineForm()}
          {renderAllClassroomsCheckbox()}
          {renderClassroomList()}
        </div>
      </div>
    )
  }

  const renderImportFromProviderButton = (theProvider: string) => {

    const theProviderTitle = providerConfigLookup[theProvider].title

    let buttonContent = <React.Fragment>Import from {theProviderTitle}</React.Fragment>
    let buttonClassName = "quill-button outlined small grey icon focus-on-light"

    if (providerClassroomsLoading && pendingImportFromProviderRequest) {
      buttonContent = <React.Fragment>Import from {theProviderTitle}<ButtonSpinner /></React.Fragment>
      buttonClassName += ' loading'
    }

    const importFromTheProvider = importFromProvider[theProvider]

    return (
      <button className={buttonClassName} onClick={importFromTheProvider} type="button">
        <img alt="" src={`${EXTRA_SMALL_ICON_BASE_SRC}/group.svg`} />
        {buttonContent}
      </button>
    )
  }

  const renderCreateAClassInlineForm = () => {
    return visibleModal === createAClassForm
      ? <CreateAClassInlineForm cancel={closeModal} onSuccess={onSuccess} />
      : null
  }

  const renderImportProviderClassroomsModal = () => {
    if (visibleModal !== importProviderClassroomsModal) { return null }

    return (
      <ImportProviderClassroomsModal
        classrooms={providerClassrooms}
        close={() => closeModal(() => setPendingImportFromProviderRequest(false))}
        onSuccess={onSuccess}
        provider={provider}
        user={user}
      />
    )
  }

  const renderImportClassesDropdown = () => {
    const options = [
      {
        label: 'Import from Clever',
        value: cleverProvider,
      },
      {
        label: 'Import from Google Classroom',
        value: googleProvider,
      },
      {
        label: 'Import from Canvas',
        value: canvasProvider,
      },
      {
        label: 'Import from ClassLink',
        value: classlinkProvider,
      }
    ]

    return (
      <DropdownInput
        ariaLabel="Import classes"
        className="import-classes-dropdown-input small bordered icon"
        handleChange={handleImportClassesClick}
        options={options}
        value={{ label: 'Import Classes', value: null }}
      />
    )
  }

  const renderLinkProviderAccountModal = () => {
    let link = ''
    let linkAccountProvider = ''

    switch (visibleModal) {
      case linkCleverAccountModal:
        link = cleverLink
        linkAccountProvider = cleverProvider
        break
      case linkGoogleAccountModal:
        // no link assignment since google uses a different component for linking accounts
        linkAccountProvider = googleProvider
        break
      case linkCanvasAccountModal:
        return <CanvasModal close={closeModal} user={user} />
      case linkClasslinkAccountModal:
        return <ClasslinkModal close={closeModal} user={user} />
      default:
        return null
    }

    return (
      <LinkProviderAccountModal
        close={closeModal}
        link={link}
        provider={linkAccountProvider}
        user={user}
      />
    )
  }

  const renderNoClassroomsToImportModal = () => {
    return visibleModal === noClassroomsToImportModal
      ? <NoClassroomsToImportModal close={closeModal} provider={provider} />
      : null
  }

  const renderReauthorizeProviderModal = () => {
    return visibleModal === reauthorizeProviderModal
      ? <ReauthorizeProviderModal close={closeModal} link={reauthorizeLink} provider={provider} />
      : null
  }

  const renderSnackbar = () => {
    return <Snackbar text={snackbarCopy} visible={isSnackbarVisible} />
  }

  return (
    <section aria-labelledby="assignStudentsHeader">
      {renderImportProviderClassroomsModal()}
      {renderReauthorizeProviderModal()}
      {renderLinkProviderAccountModal()}
      {renderNoClassroomsToImportModal()}
      {renderSnackbar()}
      {renderClassroomsSection()}
    </section>
  )
}

export default AssignStudents
